// src/pages/Users.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Input,
  Form,
  message,
  Modal,
  Card,
  Spin,
  Select,
  Grid,
  Tooltip,
  Tag,
  Avatar,
  Badge,
  Space,
  Descriptions,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  FilterOutlined,
  SearchOutlined,
  UserOutlined,
  GlobalOutlined,
  CrownOutlined,
  EuroOutlined,
  TeamOutlined,
  EyeOutlined,
  BuildOutlined,
  ToolOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  BankOutlined,
  HomeOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import { useUsers } from "../contexts/UserContext"; // your context hook
import { useNavigate } from "react-router-dom";

const { Option } = Select;
const { confirm } = Modal;
const { useBreakpoint } = Grid;

const roleOptions = [
  { value: "customer", label: "Customer" },
  { value: "admin", label: "Admin" },
  { value: "super admin", label: "Super Admin" },
  { value: "sales", label: "Sales" },
  { value: "deliveries", label: "Deliveries" },
  { value: "production manager", label: "Production Manager" },
  { value: "production admin", label: "Production Admin" },
];

const roleColors = {
  customer: "blue",
  admin: "orange",
  "super admin": "red",
  sales: "green",
  deliveries: "purple",
};

const roleIcons = {
  customer: <UserOutlined />,
  admin: <CrownOutlined />,
  "super admin": <CrownOutlined />,
  sales: <TeamOutlined />,
  deliveries: <TeamOutlined />,
  "production manager": <BuildOutlined />,
  "production admin": <ToolOutlined />,
};

const capitalizeRole = (role) => {
  if (!role) return "";
  return role
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === "" || amount === "0") return "0";
  const numericAmount = Number(amount);
  if (isNaN(numericAmount)) return "0";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericAmount);
};

const creditTermOptions = [
  { value: "30 Days", label: "30 Days" },
  { value: "Performa", label: "Performa" },
  { value: "COD (Cash on Delivery)", label: "COD (Cash on Delivery)" },
];

const Users = () => {
  const {
    users,
    loading,
    currentPage,
    pageSize,
    filters,
    currentUserRole,
    setCurrentPage,
    setFilters,
    updateUser,
    deleteUser,
  } = useUsers();

  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [saveLoading, setSaveLoading] = useState(false);
  const [filterVisible, setFilterVisible] = useState(true);

  const navigate = useNavigate();
  const screens = useBreakpoint();

  useEffect(() => {
    setFilterVisible(!screens.xs);
  }, [screens]);

  const handleEdit = (user) => {
    setEditingUser(user);

    const deliveryAddresses = Array.isArray(user.delivery_addresses)
      ? user.delivery_addresses
      : (user.delivery_addresses ? user.delivery_addresses : []);

    form.setFieldsValue({
      full_name: user.full_name,
      phone_number: user.phone_number,
      role: user.role,
      credit_terms: user.credit_terms || "30 Days",
      
      // All user fields including new ones
      company_name: user.company_name || "",
      office_address: user.office_address || "",
      office_city: user.office_city || "",
      office_eir_code: user.office_eir_code || "",
      credit_limit: user.credit_limit ?? 0,
      
      delivery_addresses: deliveryAddresses.length > 0 ? deliveryAddresses : [],
    });

    setIsModalOpen(true);
  };

  const handleViewDetails = (user) => {
    setViewingUser(user);
    setIsViewModalOpen(true);
  };

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      const values = await form.validateFields();
      
      // Updated payload with all fields
      const payload = {
        full_name: values.full_name,
        phone_number: values.phone_number,
        role: values.role,
        credit_terms: values.credit_terms,
        credit_limit: values.credit_limit, // ✅ Added credit limit
        company_name: values.company_name || "",
        office_address: values.office_address || "",
        office_city: values.office_city || "",
        office_eir_code: values.office_eir_code || "",
        delivery_addresses: Array.isArray(values.delivery_addresses)
          ? values.delivery_addresses
              .slice(0, 5)
              .map((addr) => ({
                delivery_address: addr?.delivery_address?.trim() || "",
                delivery_city: addr?.delivery_city?.trim() || "",
                delivery_eir_code: addr?.delivery_eir_code?.trim() || "",
              }))
          : [],
      };

      await updateUser(editingUser.id, payload);

      setIsModalOpen(false);
      setEditingUser(null);
    } catch (error) {
      if (error.errorFields) {
        // validation errors already shown by Form
      } else {
        message.error("Failed to update user");
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteUserWithConfirm = (userId) => {
    confirm({
      title: "Delete User Confirmation",
      content: "This action cannot be undone. Are you sure you want to delete this user?",
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: {
        danger: true,
        className: "bg-red-600 hover:bg-red-700 border-0",
      },
      cancelButtonProps: {
        className: "border-gray-300",
      },
      onOk: () => deleteUser(userId),
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setCurrentPage(1);
  };

  // ---------------------------
  // Role-permission helpers
  // ---------------------------
  // normalize currentUserRole to lowercase for safe comparison
  const normalizedCurrentRole = (currentUserRole || "").toLowerCase();

  // can user edit roles at all?
  const canEditRoles = normalizedCurrentRole === "super admin" || normalizedCurrentRole === "admin";

  // compute the options shown in the select depending on current user role:
  // - super admin: sees all roles
  // - admin: sees all roles except "super admin" (can't assign it)
  // - others: no edit (disabled)
  const availableRoleOptions = roleOptions.filter((opt) => {
    if (normalizedCurrentRole === "admin") {
      return opt.value !== "super admin";
    }
    return true; // super admin or other cases
  });

  // Filter users visibility based on viewer role + current search filters
  const filteredUsers = useMemo(() => {
    // Start from base users array
    let visible = Array.isArray(users) ? [...users] : [];

    // If current viewer is an admin (not super admin) hide super admin users entirely
    if (normalizedCurrentRole === "admin") {
      visible = visible.filter((u) => (u.role || "").toLowerCase() !== "super admin");
    }

    const field = (filters && filters.field) || "";
    const term = ((filters && filters.search) || "").toString().trim().toLowerCase();
    if (!term) return visible;

    const includes = (val) => String(val ?? "").toLowerCase().includes(term);

    return visible.filter((u) => {
      if (!field) {
        return (
          includes(u.full_name) ||
          includes(u.email) ||
          includes(u.country) ||
          includes(u.role) ||
          includes(u.credit_limit) ||
          includes(u.id) ||
          includes(u.credit_terms) ||
          includes(u.company_name)
        );
      }
      switch (field) {
        case "name":
          return includes(u.full_name);
        case "email":
          return includes(u.email);
        case "country":
          return includes(u.country);
        case "role":
          return includes(u.role);
        case "credit":
          return includes(u.credit_limit);
        case "id":
          return includes(u.id);
        case "credit_terms":
          return includes(u.credit_terms);
        case "company":
          return includes(u.company_name);
        default:
          return true;
      }
    });
  }, [users, filters, normalizedCurrentRole]);

  const getInitials = (name) => {
    return name ? name.split(" ").map((n) => n[0]).join("").toUpperCase() : "U";
  };

  const getCreditLimitColor = (amount) => {
    const numericAmount = Number(amount);
    if (isNaN(numericAmount) || numericAmount === 0) return "text-gray-500";
    if (numericAmount > 50000) return "text-green-600";
    if (numericAmount > 10000) return "text-blue-600";
    return "text-orange-600";
  };

  const columns = [
    {
      title: "ID",
      key: "srNo",
      width: 80,
      render: (_, __, index) => (
        <div className="text-gray-500 font-mono text-sm">
          #{(currentPage - 1) * pageSize + index + 1}
        </div>
      ),
    },
    {
      title: "User",
      key: "name",
      render: (_, record) => (
        <div className="flex items-center space-x-3">
          <Badge dot color={record.status === "active" ? "green" : "red"} offset={[-2, 32]}>
            <Avatar size="large" className="bg-gradient-to-r from-blue-500 to-blue-600 shadow-md">
              {getInitials(record.full_name)}
            </Avatar>
          </Badge>
          <div>
            <div className="font-semibold text-gray-900">{record.full_name}</div>
            <div className="text-xs text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      render: (v) => <span className="text-gray-700">{v || "-"}</span>,
    },
    {
      title: "Contact",
      dataIndex: "phone_number",
      key: "phone_number",
      render: (phoneNumber) => (
        <div className="flex items-center space-x-2">
          <span className="text-gray-600">{phoneNumber || "Not provided"}</span>
        </div>
      ),
    },
    {
      title: "Location",
      dataIndex: "country",
      key: "country",
      render: (country) => (
        <div className="flex items-center space-x-2">
          <GlobalOutlined className="text-gray-400" />
          <span className="text-gray-600">{country || "Not specified"}</span>
        </div>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag
          color={roleColors[role] || "default"}
          className="px-3 py-1 rounded-full text-xs font-semibold border-0 flex items-center space-x-1"
          icon={roleIcons[role]}
        >
          {capitalizeRole(role)}
        </Tag>
      ),
    },
    {
      title: "Credit Terms",
      dataIndex: "credit_terms",
      key: "credit_terms",
      width: 160,
      render: (credit_terms) => (
        <Tag className="rounded-full text-xs font-semibold" color={credit_terms === "30 Days" ? "blue" : credit_terms === "Performa" ? "gold" : "green"}>
          {credit_terms || "30 Days"}
        </Tag>
      ),
    },
    {
      title: "Credit Limit",
      dataIndex: "credit_limit",
      key: "credit_limit",
      render: (creditLimit) => {
        return (
          <div className={`flex items-center space-x-2 bg-gradient-to-r from-gray-50 to-gray-100 px-3 py-2 rounded-lg border border-gray-200 min-w-[140px] ${getCreditLimitColor(creditLimit)}`}>
            <EuroOutlined />
            <span className="font-semibold">{formatCurrency(creditLimit)}</span>
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) => (
        <div className="flex items-center space-x-1">
          <Tooltip title="Edit User">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              className="hover:bg-blue-50 rounded-lg transition-colors"
            />
          </Tooltip>
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
              className="hover:bg-green-50 rounded-lg transition-colors"
            />
          </Tooltip>
          <Tooltip title="Delete User">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteUserWithConfirm(record.id)}
              className="hover:bg-red-50 rounded-lg transition-colors"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-white rounded-xl shadow-sm">
                <TeamOutlined className="text-blue-600 text-xl" />
              </div>
              User Management
            </h1>
            <p className="text-gray-600 mt-2">Manage and monitor all user accounts and permissions</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-gray-600">Total:</span>
                <span className="font-semibold text-gray-900">{filteredUsers.length} users</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <Card className="border-0 shadow-sm rounded-2xl bg-white overflow-hidden" bodyStyle={{ padding: "24px" }}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row gap-3">
                <Select
                  placeholder="Filter by field"
                  value={(filters && filters.field) || undefined}
                  onChange={(value) => handleFilterChange("field", value)}
                  allowClear
                  className="w-full sm:w-48 rounded-xl"
                  suffixIcon={<FilterOutlined className="text-gray-400" />}
                  size="large"
                >
                  <Option value="name">Name</Option>
                  <Option value="email">Email</Option>
                  <Option value="country">Country</Option>
                  <Option value="role">Role</Option>
                  <Option value="credit">Credit Limit</Option>
                  <Option value="id">User ID</Option>
                  <Option value="credit_terms">Credit Terms</Option>
                  <Option value="company">Company Name</Option>
                </Select>

                <Input
                  placeholder="Search users..."
                  value={(filters && filters.search) || ""}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  allowClear
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full sm:flex-1 rounded-xl"
                  size="large"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Users Table Card */}
        <Card className="border-0 shadow-sm rounded-2xl bg-white overflow-hidden" bodyStyle={{ padding: 0 }}>
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10 rounded-2xl">
              <div className="text-center">
                <Spin size="large" />
                <p className="mt-3 text-gray-600 font-medium">Loading users data...</p>
              </div>
            </div>
          )}

          <div className="relative">
            <Table
              columns={columns}
              dataSource={filteredUsers}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize,
                total: filteredUsers.length,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
                className: "px-6 py-4 border-t border-gray-200",
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
              }}
              scroll={{ x: 1300 }}
              className="custom-table"
              rowClassName="hover:bg-gray-50 transition-colors"
            />
          </div>
        </Card>
      </div>

      {/* Edit User Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <EditOutlined className="text-blue-600 text-lg" />
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900">Edit User</div>
              <div className="text-sm text-gray-500">Update user information including credit limit</div>
            </div>
          </div>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)} className="px-6 rounded-lg h-10 border-gray-300">
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            loading={saveLoading}
            className="px-6 rounded-lg h-10 bg-blue-600 hover:bg-blue-700 border-0 font-medium"
          >
            Save Changes
          </Button>,
        ]}
        className="rounded-2xl"
        width={800}
      >
        <Form form={form} layout="vertical" className="mt-6 space-y-4" initialValues={{ credit_terms: "30 Days" }}>
          <Form.Item
            label="Full Name"
            name="full_name"
            rules={[
              { required: true, message: "Please enter full name" },
              {
                pattern: /^[A-Za-z\s]+$/,
                message: "Full Name should contain only letters and spaces",
              },
            ]}
          >
            <Input placeholder="Enter full name" className="rounded-lg h-11" size="large" />
          </Form.Item>

          <Form.Item label="Email">
            <Input value={editingUser?.email} disabled className="rounded-lg h-11 bg-gray-50" size="large" />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="phone_number"
            rules={[
              { required: true, message: "Please enter phone number" },
              { pattern: /^\d+$/, message: "Phone number must be numeric" },
              {
                validator: (_, value) =>
                  value && value.length > 15 ? Promise.reject("Phone number cannot exceed 15 digits") : Promise.resolve(),
              },
            ]}
          >
            <Input placeholder="Enter phone number" maxLength={15} className="rounded-lg h-11" size="large" />
          </Form.Item>

          {/* Company Information Section */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="text-sm font-semibold mb-3">Company Information</div>
            <Form.Item label="Company Name" name="company_name">
              <Input placeholder="Enter company name" className="rounded-lg h-11" size="large" />
            </Form.Item>

            <Form.Item label="Office Address" name="office_address">
              <Input placeholder="Enter office address" className="rounded-lg h-11" size="large" />
            </Form.Item>

            <div className="grid grid-cols-2 gap-3">
              <Form.Item label="Office City" name="office_city">
                <Input placeholder="City" />
              </Form.Item>

              <Form.Item label="Office Eir Code" name="office_eir_code">
                <Input placeholder="Eir Code" />
              </Form.Item>
            </div>
          </div>

          <Form.Item label="Role" name="role">
            {canEditRoles ? (
              <Select placeholder="Select role" className="rounded-lg h-11" size="large">
                {availableRoleOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    <div className="flex items-center space-x-2">
                      {roleIcons[opt.value]}
                      <span>{opt.label}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            ) : (
              <Select disabled className="rounded-lg h-11 bg-gray-50" size="large">
                <Option value={editingUser?.role}>{capitalizeRole(editingUser?.role)}</Option>
              </Select>
            )}
          </Form.Item>

          {/* Credit Terms and Credit Limit Section */}
          <div className="grid grid-cols-2 gap-4">
            <Form.Item label="Credit Terms" name="credit_terms" rules={[{ required: true, message: "Please select credit terms" }]}>
              <Select placeholder="Select credit terms" className="rounded-lg h-11" size="large">
                {creditTermOptions.map((o) => (
                  <Option key={o.value} value={o.value}>
                    {o.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            
            <Form.Item label="Credit Limit" name="credit_limit">
              <Input
                type="number"
                prefix={<EuroOutlined />}
                placeholder="Enter credit limit"
                className="rounded-lg h-11"
                size="large"
                min={0}
              />
            </Form.Item>
          </div>

          {/* Delivery Addresses - structured blocks */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold">Delivery Addresses</div>
              <div className="text-xs text-gray-500">Max 5 entries</div>
            </div>

            <Form.List name="delivery_addresses">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div key={field.key} className="mb-4 pb-3 border-b last:border-b-0">
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium">Address #{index + 1}</div>
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        </div>

                        <Form.Item
                          {...field}
                          name={[field.name, "delivery_address"]}
                        >
                          <Input placeholder="Delivery address (optional)" />
                        </Form.Item>

                        <div className="grid grid-cols-2 gap-3">
                          <Form.Item
                            {...field}
                            name={[field.name, "delivery_city"]}
                          >
                            <Input placeholder="City (optional)" />
                          </Form.Item>

                          <Form.Item
                            {...field}
                            name={[field.name, "delivery_eir_code"]}
                            rules={[
                              { max: 20, message: "Eir code too long" },
                            ]}
                          >
                            <Input placeholder="Eir Code (optional)" />
                          </Form.Item>
                        </div>
                      </Space>
                    </div>
                  ))}

                  <Form.Item>
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          delivery_address: "",
                          delivery_city: "",
                          delivery_eir_code: "",
                        })
                      }
                      disabled={fields.length >= 5}
                      block
                    >
                      Add Delivery Address
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
        </Form>
      </Modal>

      {/* View Details Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <EyeOutlined className="text-green-600 text-lg" />
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900">User Details</div>
              <div className="text-sm text-gray-500">Complete information about the user</div>
            </div>
          </div>
        }
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalOpen(false)} className="px-6 rounded-lg h-10 border-gray-300">
            Close
          </Button>,
          <Button
            key="edit"
            type="primary"
            onClick={() => {
              setIsViewModalOpen(false);
              handleEdit(viewingUser);
            }}
            className="px-6 rounded-lg h-10 bg-blue-600 hover:bg-blue-700 border-0 font-medium"
          >
            Edit User
          </Button>,
        ]}
        className="rounded-2xl"
        width={700}
      >
        {viewingUser && (
          <div className="mt-6 space-y-6">
            {/* User Profile Header */}
            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Avatar size={64} className="bg-gradient-to-r from-blue-500 to-blue-600">
                {getInitials(viewingUser.full_name)}
              </Avatar>
              <div>
                <div className="text-xl font-bold text-gray-900">{viewingUser.full_name}</div>
                <div className="flex items-center space-x-2 mt-1">
                  <MailOutlined className="text-gray-400" />
                  <span className="text-gray-600">{viewingUser.email}</span>
                </div>
                <div className="flex items-center space-x-2 mt-1">
                  <PhoneOutlined className="text-gray-400" />
                  <span className="text-gray-600">{viewingUser.phone_number || "Not provided"}</span>
                </div>
              </div>
            </div>

            {/* Main Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Role & Status</div>
                  <div className="flex items-center justify-between">
                    <Tag
                      color={roleColors[viewingUser.role] || "default"}
                      className="px-3 py-1 rounded-full text-xs font-semibold border-0 flex items-center space-x-1"
                      icon={roleIcons[viewingUser.role]}
                    >
                      {capitalizeRole(viewingUser.role)}
                    </Tag>
                    <Badge
                      status={viewingUser.status === "active" ? "success" : "error"}
                      text={viewingUser.status === "active" ? "Active" : "Inactive"}
                    />
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Financial Information</div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Credit Terms:</span>
                      <Tag color={viewingUser.credit_terms === "30 Days" ? "blue" : viewingUser.credit_terms === "Performa" ? "gold" : "green"}>
                        {viewingUser.credit_terms || "30 Days"}
                      </Tag>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Credit Limit:</span>
                      <div className={`font-bold ${getCreditLimitColor(viewingUser.credit_limit)}`}>
                        €{formatCurrency(viewingUser.credit_limit)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <BankOutlined className="mr-2" />
                    Company Information
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Company:</span>
                      <span className="font-medium">{viewingUser.company_name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Country:</span>
                      <span className="font-medium">{viewingUser.country || "Not specified"}</span>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <EnvironmentOutlined className="mr-2" />
                    Office Address
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm">{viewingUser.office_address || "Not provided"}</div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{viewingUser.office_city || ""}</span>
                      <span>{viewingUser.office_eir_code || ""}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery Addresses */}
            {viewingUser.delivery_addresses && viewingUser.delivery_addresses.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <ShopOutlined className="mr-2" />
                  Delivery Addresses ({viewingUser.delivery_addresses.length})
                </div>
                <div className="space-y-3">
                  {Array.isArray(viewingUser.delivery_addresses) && viewingUser.delivery_addresses.map((addr, index) => (
                    <div key={index} className="p-3 bg-white rounded border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <div className="text-sm font-medium text-gray-800">Address #{index + 1}</div>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="text-gray-700">{addr.delivery_address || "Not specified"}</div>
                        <div className="flex justify-between text-gray-600">
                          <span>{addr.delivery_city || ""}</span>
                          <span>{addr.delivery_eir_code || ""}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Info */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm font-semibold text-blue-700 mb-2 flex items-center">
                <IdcardOutlined className="mr-2" />
                Additional Information
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-600">User ID:</div>
                  <div className="font-mono font-medium">{viewingUser.id}</div>
                </div>
                <div>
                  <div className="text-gray-600">Invoice Email:</div>
                  <div className="font-medium">{viewingUser.invoice_email || viewingUser.email}</div>
                </div>
                <div>
                  <div className="text-gray-600">Subscription:</div>
                  <div className="font-medium">
                    {viewingUser.subscribe ? (
                      <Tag color="green">Subscribed</Tag>
                    ) : (
                      <Tag color="default">Not Subscribed</Tag>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-gray-600">Account Status:</div>
                  <div className="font-medium">
                    {viewingUser.isApproved ? (
                      <Tag color="green">Approved</Tag>
                    ) : (
                      <Tag color="orange">Pending Approval</Tag>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Users;
